/**
 * The signed-in portal principal — `{ kind, operator_name, driver_name, … }`.
 *
 * Enrolment stores it, so there is always something to paint on the first
 * frame and a portal screen never opens empty.
 *
 * WHY IT STILL ASKS THE API
 * -------------------------
 * What enrolment stored is a SNAPSHOT of the API's session view taken when
 * that login happened, and the session view GROWS. `may_assume_driver` — may
 * this administrator open a driver's own screen — arrived months after the
 * first of them signed in, and `GET /portal/session` is the only thing that
 * computes it (`PortalController.getSession` → `canAssumeDriver`); enrolment
 * does not, and never did.
 *
 * So a cached principal is a first paint and nothing more. Treating it as the
 * whole truth is what hid "Open their screen" from the administrators it was
 * built for: their stored principal had a `kind`, this hook called itself
 * satisfied, and the session was never read again — for weeks.
 *
 * AND WHY IT CANNOT WAIT TO BE ASKED
 * ----------------------------------
 * Noticing the field is missing does not work. The API sends it only when it
 * is TRUE (`may_assume_driver: mayAssumeDriver || undefined`), so an absent
 * key means either "cached before the capability existed" or "this is a
 * driver, and never could" — the same two bytes for both. A client that
 * refetched because the key was absent would refetch on every page load, for
 * every driver and operator, forever.
 *
 * Hence: ask once per app load, whatever is cached, and let the cached copy
 * cover the wait. One request, no flash, and a capability added to the
 * session view tomorrow reaches this screen without anybody editing this
 * file.
 */
import { useEffect, useRef, useState } from 'react';
import { fetchPortalSession, getAssumedDriver, getPortalPrincipal } from '../../../Services/PortalApi';

/**
 * This app load's `GET /portal/session`.
 *
 * Shared by every mounted hook, so a screen carrying three of them (the
 * guard, the frame, a button on a card) asks once between them, and moving
 * from the board to a trip and back does not ask again.
 */
let session = null;

/** The request currently in the air, or null. See `refreshPrincipal`. */
let inFlight = null;

/**
 * WHO `session` is an answer about: the driver being acted as, or '' for the
 * login's own self. The one thing that can make a shared answer wrong.
 */
let askedAbout = null;

/** Who a request made right now would be about. */
const subjectNow = () => getAssumedDriver()?.id || '';

const refreshPrincipal = () => {
  const subject = subjectNow();

  // Acting as somebody else — or stopping — changes who the answer is about,
  // and what we are holding is an answer about a different person.
  //
  // Checked on the SUBJECT and not on storage, because storage cannot tell
  // the difference while a request is still in the air. That window is
  // precisely the click that matters: an administrator whose cached
  // principal already carries `may_assume_driver` sees "Open their screen"
  // on the first frame, so they can press it before this load's own
  // /portal/session has come back. Keeping that answer would resolve the
  // guard to `dispatcher` on a driver URL and send them straight back to the
  // board.
  if (subject !== askedAbout) session = null;

  // Nothing in storage and nothing in the air: the principal this load
  // settled on has been dropped, which is how PortalApi says "somebody else
  // now" — signing out clears it, and so does starting or stopping an
  // impersonation. Ask again.
  //
  // `inFlight` is what keeps a COLD start to one request: storage is empty
  // for as long as the very first request is in the air, and each hook
  // mounting in that window would otherwise start its own.
  if (!inFlight && !getPortalPrincipal()) session = null;

  if (!session) {
    askedAbout = subject;
    const asked = fetchPortalSession().finally(() => {
      // Only while it is still THE request: an older one settling must not
      // report the air clear when this load's real request is still in it.
      if (inFlight === asked) inFlight = null;
    });
    inFlight = asked;
    session = asked;
  }

  return session;
};

/**
 * Test seam — forgets this load's session so a spec starts cold. Production
 * has no use for it: an app load is a page load.
 */
export const resetPortalPrincipalRefresh = () => {
  session = null;
  inFlight = null;
  askedAbout = null;
};

export default function usePortalPrincipal() {
  const [principal, setPrincipal] = useState(() => getPortalPrincipal());
  const [error, setError] = useState(null);
  const known = Boolean(principal?.kind);
  // What there was to paint when this hook mounted, read once. It decides
  // whether a refresh that fails is the screen's whole story or nothing at
  // all, and it must not move under us when the refresh lands.
  const hadCached = useRef(known);

  useEffect(() => {
    let cancelled = false;

    refreshPrincipal()
      .then((fresh) => {
        if (!cancelled && fresh) setPrincipal(fresh);
      })
      .catch((failure) => {
        // A cached principal is enough to render, so a refresh that could not
        // be made — offline, a 5xx — changes nothing on screen, exactly as it
        // changed nothing when this hook did not refresh at all. It is only
        // when there is NOTHING to paint that the failure is the whole story
        // and the guard has to say so.
        if (!cancelled && !hadCached.current) setError(failure);
      });

    return () => { cancelled = true; };
    // Once, on mount: `refreshPrincipal` owns how often the API is really
    // asked, and re-running this per render would only re-read its latch.
  }, []);

  return {
    principal,
    kind: principal?.kind || null,
    loading: !known && !error,
    error,
  };
}
